import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { ArrowUp, Users } from "lucide-react";
import ReportButton from "@/components/ReportButton";
import PromoteButton from "@/components/PromoteButton";
import type { Tables } from "@/integrations/supabase/types";

type Product = Tables<"products">;

interface SponsorInfo {
  name: string | null;
  avatar_url: string | null;
}

interface ProductCardProps {
  product: Product;
  upvoteCount: number;
  interestCount: number;
  showPromote?: boolean;
  sponsor?: SponsorInfo | null;
}

const stageLabels: Record<string, string> = { idea: "Ideia", mvp: "MVP", launched: "Lançado" };
const stageColors: Record<string, string> = {
  idea: "bg-accent text-accent-foreground",
  mvp: "bg-primary/10 text-primary",
  launched: "bg-secondary/10 text-secondary",
};

const formatTimeLeft = (until: string, t: (k: string, opts?: any) => string) => {
  const ms = new Date(until).getTime() - Date.now();
  if (ms <= 0) return null;
  const hours = Math.floor(ms / (1000 * 60 * 60));
  if (hours >= 1) return t("promote.timeLeft", { hours });
  const minutes = Math.max(1, Math.floor(ms / (1000 * 60)));
  return t("promote.minutesLeft", { minutes });
};

const ProductCard = ({ product, upvoteCount, interestCount, showPromote = true, sponsor }: ProductCardProps) => {
  const { t } = useTranslation();
  const featuredUntil = (product as any).is_featured_until as string | null;
  const isFeatured = featuredUntil && new Date(featuredUntil) > new Date();
  const timeLeft = isFeatured ? formatTimeLeft(featuredUntil!, t) : null;
  const sponsorName = sponsor?.name?.trim() || null;
  const isDemo = (product as any).is_demo === true;

  return (
    <div className="relative group">
      <Link
        to={`/products/${product.id}`}
        className={`block rounded-lg border bg-card shadow-card hover:shadow-card-hover transition-all duration-300 overflow-hidden ${
          isFeatured ? "border-primary ring-1 ring-primary" : "border-border"
        }`}
      >
        {isFeatured && (
          <div className="gradient-primary text-primary-foreground text-xs font-semibold py-1 px-3 space-y-0.5">
            <div className="flex items-center justify-between">
              <span>🚀 {t("promote.sponsoredBadge")}</span>
              {timeLeft && <span className="opacity-90">{timeLeft}</span>}
            </div>
            {sponsorName && (
              <div className="text-[10px] opacity-90 truncate">
                {t("promote.sponsoredBy", { name: sponsorName })}
              </div>
            )}
          </div>
        )}

        <div className="h-32 bg-muted overflow-hidden">
          {product.cover_url ? (
            <img src={product.cover_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
          ) : (
            <div className="w-full h-full gradient-primary opacity-20" />
          )}
        </div>

        <div className="p-4 space-y-3">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center shrink-0 overflow-hidden">
              {product.logo_url ? (
                <img src={product.logo_url} alt={product.name} className="h-full w-full object-cover" />
              ) : (
                <span className="text-lg font-bold text-primary">{product.name[0]}</span>
              )}
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-card-foreground truncate group-hover:text-primary transition-colors">
                {product.name}
              </h3>
              <p className="text-sm text-muted-foreground truncate">{product.tagline}</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-1.5">
            <Badge variant="secondary" className={stageColors[product.stage]}>
              {stageLabels[product.stage]}
            </Badge>
            {isDemo && (
              <Badge variant="outline" className="text-xs border-dashed text-muted-foreground">
                {t("demo.badge")}
              </Badge>
            )}
            {product.categories.slice(0, 2).map((cat) => (
              <Badge key={cat} variant="outline" className="text-xs">{cat}</Badge>
            ))}
          </div>

          {product.tech_stack.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {product.tech_stack.slice(0, 3).map((tech) => (
                <span key={tech} className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{tech}</span>
              ))}
              {product.tech_stack.length > 3 && (
                <span className="text-xs text-muted-foreground">+{product.tech_stack.length - 3}</span>
              )}
            </div>
          )}

          <div className="flex items-center justify-between gap-2 pt-2 border-t border-border">
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <ArrowUp className="h-3.5 w-3.5" /> {upvoteCount}
              </span>
              <span className="flex items-center gap-1">
                <Users className="h-3.5 w-3.5" /> {interestCount}
              </span>
            </div>
            {showPromote && (
              <div onClick={(e) => e.preventDefault()}>
                <PromoteButton productId={product.id} size="sm" variant="ghost" className="h-7 px-2 text-xs" />
              </div>
            )}
          </div>
        </div>
      </Link>

      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
        <ReportButton productId={product.id} variant="ghost" />
      </div>
    </div>
  );
};

export default ProductCard;
